import type { EffectRegion } from "@client/types/vision";
import { useId } from "react";
import type { FC } from "react";
import { RegionControls } from "./RegionControls";

interface BlurrinessToolProps {
	sigma: number;
	roiSigma: number;
	onChange: (params: { sigma: number }) => void;
	onRoiSigmaChange: (value: number) => void;
	effectRegion: EffectRegion | null;
	regionDrawMode: "none" | "drawing";
	regionPoints: Array<{ x: number; y: number }>;
	onStartDrawing: () => void;
	onUndoPoint: () => void;
	onCancelDrawing: () => void;
	onClearRegion: () => void;
}

export const BlurrinessTool: FC<BlurrinessToolProps> = ({
	sigma,
	roiSigma,
	onChange,
	onRoiSigmaChange,
	effectRegion,
	regionDrawMode,
	regionPoints,
	onStartDrawing,
	onUndoPoint,
	onCancelDrawing,
	onClearRegion,
}) => {
	const sliderId = useId();

	const handleDecrease = () => {
		const newValue = Math.max(0, sigma - 0.1);
		onChange({ sigma: Number(newValue.toFixed(2)) });
	};

	const handleIncrease = () => {
		const newValue = Math.min(10.0, sigma + 0.1);
		onChange({ sigma: Number(newValue.toFixed(2)) });
	};

	return (
		<div className="p-4">
			<div className="mb-3 font-semibold">Blurriness</div>

			<RegionControls
				effectRegion={effectRegion}
				regionDrawMode={regionDrawMode}
				regionPoints={regionPoints}
				onStartDrawing={onStartDrawing}
				onUndoPoint={onUndoPoint}
				onCancelDrawing={onCancelDrawing}
				onClearRegion={onClearRegion}
			/>

			{/* Region slider FIRST when a region is drawn (the user's primary
			    intent after drawing). Base slider second. When no region, only
			    base slider renders. */}
			{effectRegion && (
				<div className="mb-3">
					<div className="flex items-center justify-center min-h-[36px] mb-1 px-1">
						<span className="text-lg font-bold tabular-nums">
							Region σ {roiSigma.toFixed(1)}
						</span>
					</div>
					<div className="flex items-center gap-1">
						<button
							onClick={() =>
								onRoiSigmaChange(Number(Math.max(0, roiSigma - 0.1).toFixed(2)))
							}
							className="flex-shrink-0 w-11 h-11 text-xl font-bold bg-blue-500 text-white rounded hover:bg-blue-600 active:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
							disabled={roiSigma <= 0}
							aria-label="Decrease region blur"
						>
							−
						</button>
						<input
							type="range"
							min={0}
							max={10.0}
							step={0.05}
							value={roiSigma}
							onChange={(e) => onRoiSigmaChange(Number(e.target.value))}
							onInput={(e) =>
								onRoiSigmaChange(Number((e.target as HTMLInputElement).value))
							}
							className="flex-1 min-w-0"
							aria-label="Region blur slider"
						/>
						<button
							onClick={() =>
								onRoiSigmaChange(
									Number(Math.min(10, roiSigma + 0.1).toFixed(2)),
								)
							}
							className="flex-shrink-0 w-11 h-11 text-xl font-bold bg-blue-500 text-white rounded hover:bg-blue-600 active:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
							disabled={roiSigma >= 10.0}
							aria-label="Increase region blur"
						>
							+
						</button>
					</div>
				</div>
			)}

			<div className="flex items-center justify-center min-h-[36px] mb-1 px-1">
				<span className="text-lg font-bold tabular-nums">
					{effectRegion ? "Base " : ""}σ {sigma.toFixed(1)}
				</span>
			</div>
			<div className="flex items-center gap-1 mb-2">
				<button
					onClick={handleDecrease}
					className="flex-shrink-0 w-11 h-11 text-xl font-bold bg-blue-500 text-white rounded hover:bg-blue-600 active:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
					disabled={sigma <= 0}
					aria-label="Decrease blurriness"
				>
					−
				</button>
				<input
					type="range"
					min={0}
					max={10.0}
					step={0.05}
					id={sliderId}
					value={sigma}
					onChange={(e) => onChange({ sigma: Number(e.target.value) })}
					onInput={(e) =>
						onChange({ sigma: Number((e.target as HTMLInputElement).value) })
					}
					className="flex-1 min-w-0"
				/>
				<button
					onClick={handleIncrease}
					className="flex-shrink-0 w-11 h-11 text-xl font-bold bg-blue-500 text-white rounded hover:bg-blue-600 active:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
					disabled={sigma >= 10.0}
					aria-label="Increase blurriness"
				>
					+
				</button>
			</div>
		</div>
	);
};
